import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';

import Markdown from 'preact-markdown';
import linkstate from 'linkstate';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { printViaIframe } from '../../lib/printHelper';
import { resolveCoveredAmountInput } from '../../lib/insuranceClaimAmount';

import FormFields from '../../components/form/fields';
import Input from '../../components/form/input';

import DyrejournalSaleReceipt from '../../components/dyrejournal/saleReceipt';
import PrintedMark from '../../components/dyrejournal/printedMark';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

@withText(props => ({
    orderNumber: <Text id='input.orderNumber-help'>orderNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
    installation: <Text id='input.installation-help'>installation</Text>,
    url: <Text id='input.url-help'>url</Text>,
}))
@observer
class SalePaymentOther extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            payDate: util.isoDate(new Date(), true),
            insurancePaidDate: util.isoDate(new Date(), false, false, true),
            // TICKET-01637: how the egenandel child sale is created on settlement.
            // 'invoice' is the historical default; 'inProgress' leaves a plain open
            // sale so the owner can pay by card/cash/Vipps at the counter.
            egenandelMode: 'invoice',
            // TICKET-01651: first animal + journal presence resolved through the
            // sub-sales for a samlefaktura (which stores neither itself).
            claimContextAnimal: null,
            claimContextHasJournals: false,
        };
    }

    loadClaimContext = async (newSale) => {
        if (!newSale || !newSale.id) {
            return;
        }
        const hasSubInvoices = Array.isArray(newSale.subInvoices) && newSale.subInvoices.length > 0;
        const hasAnimals = Array.isArray(newSale.animals) && newSale.animals.filter(Boolean).length > 0;
        if (!hasSubInvoices || hasAnimals) {
            return;
        }
        const response = await util.fetchApi(`/api/sales/${newSale.id}/insurance-claim/context`, { method: 'GET' }, {});
        if (!response || response.status !== 200 || !response.data) {
            return;
        }
        const { animals = [], journals = [] } = response.data;
        this.setState({
            claimContextAnimal: animals.filter(Boolean)[0] || null,
            claimContextHasJournals: journals.filter(Boolean).length > 0,
        });
    }

    changePayDate = async e => {
        const { value: payDate } = e.target;
        this.setState({ payDate });
    }

    savePayment = async (e) => {
        const { partialAmount, payDate = new Date() } = this.state;
        this.setState({ paymentInProgress: true });
        const { method } = e.target.closest('button').dataset;
        const { callback = () => {} } = this.props;
        const { saleStore, salePaymentStore } = this.props.stores;
        const { newSale } = saleStore;
        if (partialAmount > 0) {
            // saveField(id, field, value, updateMemory, opt = {}) {
            // await saleStore.saveField(newSale.id, 'paymentAmount', 'vipps', true);
            // await saleStore.saveField(newSale.id, 'paymentAmount', 'vipps', true);
            // await saleStore.save({
            //     id: newSale.id,
            //     paymentAmount: partialAmount,
            //     paymentMethod: method,
            // });

            // Save salePayment
            await salePaymentStore.insert({
                sale: newSale.id,
                amount: method === 'creditNote' ? 0 - partialAmount : partialAmount,
                vat: 0,
                paymentMethod: method,
                paidDate: payDate,
            });

            const partialPayments = newSale.partialPayments || [];
            saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'partial');
            saleStore.updateObjectKeyValue('newSale', 'paymentAmount', parseInt(partialAmount, 10));
            saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('partial'));
            saleStore.updateObjectKeyValue('newSale', 'partialPayments', [...partialPayments, {
                amount: parseInt(partialAmount, 10),
                vat: 0,
                date: payDate,
                paymentMethod: method,
            }]);
        } else {
            const totalAmount = saleStore.sumTotal(newSale.products, false);
            // Save salePayment
            await salePaymentStore.insert({
                sale: newSale.id,
                amount: method === 'creditNote' ? 0 - totalAmount : totalAmount,
                vat: 0,
                paymentMethod: method,
                paidDate: payDate,
            });

            saleStore.updateObjectKeyValue('newSale', 'paymentMethod', method);
            if (method === 'cash' || method === 'card' || method === 'cardExternal' || method === 'vipps' || method === 'account') {
                // TODO: Add partial payment support
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('paid'));
                saleStore.updateObjectKeyValue('newSale', 'paidDate', payDate);
            } else if (method === 'invoice') {
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('invoice'));
            } else if (method === 'account') {
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('account'));
            } else if (method === 'creditNote') {
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('creditNote'));
            }
        }
        await saleStore.saveSale({ calledFrom: 'salePayment.savePayment', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        this.setState({ paymentInProgress: false });
        callback(true);
    }

    saveInvoice = async (e) => {
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'invoice');
        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('invoice'));

        await saleStore.saveSale({ calledFrom: 'salePayment.saveInvoice', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        this.setState({ paymentInProgress: false });
        callback(true);
        this.sendInvoice();
    }

    sendInvoice = async e => {
        const { drawerLevel = 1 } = this.props;
        // console.log('onClickEmailReminder', e);
		const { appState, saleStore } = this.props.stores;
        const { newSale } = saleStore;
        this.setState({
            emailSaleSending: true,
            emailSaleSendStatus: null,
        });

        // const response = await saleStore.emailReminder({ sale: newSale });
		const { drawerHeightMedium } = appState;
        appState.openDrawer('sendInvoice', {
			height: drawerHeightMedium,
            newSale,
            callback: (response) => {
                this.setState({
                    emailSaleSending: false,
                    emailSaleSendStatus: response?.status,
                });
                clearTimeout(this.emailTimer);

                this.emailTimer = setTimeout(() => {
                    // console.log('emailTimer');
                    this.setState({
                        emailSaleSending: false,
                        emailSaleSendStatus: null,
                    });
                }, 2000);
            }
		}, drawerLevel + 1);
    }

    createCreditNote = async (e) => {
        // 1. Mark current sale as invoice
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'invoiceCredited');
        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('invoiceCredited'));

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNote', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        this.setState({ paymentInProgress: false });

        // 2. Create new sale with negative amount and status creditNote
        const oldSale = { ...newSale };
        delete oldSale.id;
        delete oldSale.createdDate;
        delete oldSale.saleDate;
        oldSale.products = oldSale.products.map(e => {
            const p = { ...e };
            p.discountAmount = 0 - p.discountAmount;
            if (typeof p.discountLineAmount === 'number') p.discountLineAmount = 0 - p.discountLineAmount;
            p.price = 0 - p.price;
            // Negate segment prices for credit note
            if (p.medicine) p.medicine = 0 - p.medicine;
            if (p.fee) p.fee = 0 - p.fee;
            if (p.equipment) p.equipment = 0 - p.equipment;
            if (p.other) p.other = 0 - p.other;
            return p;
        });

        saleStore.createNewSale({ user: oldSale.user });
        saleStore.updateKeyValue('newSale', {
            ...oldSale,
            paymentMethod: 'creditNote',
            status: saleStore.getSaleStatus('creditNote'),
        });

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNote', event: e });
        callback(true);
    }

    createCreditNoteSale = async (e) => {
        // 1. Mark current sale as invoice
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'saleCredited');
        // saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('saleCredited'));

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNoteSale', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        this.setState({ paymentInProgress: false });

        // 2. Create new sale with negative amount and status creditNote
        const oldSale = { ...newSale };
        delete oldSale.id;
        delete oldSale.createdDate;
        delete oldSale.saleDate;
        oldSale.products = oldSale.products.map(e => {
            const p = { ...e };
            p.discountAmount = 0 - p.discountAmount;
            if (typeof p.discountLineAmount === 'number') p.discountLineAmount = 0 - p.discountLineAmount;
            p.price = 0 - p.price;
            // Negate segment prices for credit note
            if (p.medicine) p.medicine = 0 - p.medicine;
            if (p.fee) p.fee = 0 - p.fee;
            if (p.equipment) p.equipment = 0 - p.equipment;
            if (p.other) p.other = 0 - p.other;
            return p;
        });

        saleStore.createNewSale({ user: oldSale.user });
        saleStore.updateKeyValue('newSale', {
            ...oldSale,
            paymentMethod: 'creditNote',
            status: 0,
        });

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNoteSale', event: e });
        callback(true);
    }

    unlockPaymentMethod = async (e) => {
        const { saleStore } = this.props.stores;
        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', null);
        saleStore.updateObjectKeyValue('newSale', 'paidDate', null);
        saleStore.updateObjectKeyValue('newSale', 'paymentAmount', 0);
        saleStore.updateObjectKeyValue('newSale', 'status', 0);
        await saleStore.saveSale({ calledFrom: 'salePayment.unlockPaymentMethod', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
    }

    togglePartialPayment = e => {
        this.setState({ partialPayment: !this.state.partialPayment });
    }

    loadSalePayments = async saleId => {
        if (!util.isNumber(saleId)) {
            return false;
        }
        const { salePaymentStore, saleStore } = this.props.stores;
        const response = await salePaymentStore.load({
            query: {
                sale: saleId,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('currentPayments', response);
    }

    onClickDeleteSalePayment = async e => {
        e.preventDefault();
        e.stopPropagation();
        const dialogAction = beginDialogAction(this, 'onClickDeleteSalePayment', () => [
            this.props.stores.saleStore.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const dialogTargetData = { ...e.target.closest('tr').dataset };



            if (!((await appConfirm('Are you sure you want to delete this salePayment?')) && dialogAction.isCurrent())) {
                return;
            }

            const { id } = dialogTargetData;
            const { salePaymentStore, saleStore } = this.props.stores;
            // console.log('onClickDeleteSalePayment', id, parseInt(id, 10));
            const { newSale } = saleStore;
            await salePaymentStore.delete(parseInt(id, 10));

            setTimeout(() => {
                if (newSale?.id) {
                    this.loadSalePayments(newSale.id);
                }
            }, 1000);
        } finally {
            dialogAction.finish();
        }
    }

    saveInvoiceAsLost = async e => {
        const dialogAction = beginDialogAction(this, 'saveInvoiceAsLost', () => [
            this.props.stores.saleStore.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {
            if (!((await appConfirm('Er du sikker på at du vil markere dette salget som tapt? PS! Husk å følge nasjonale skatteregler for dette.')) && dialogAction.isCurrent())) {
                return;
            }
            this.setState({ paymentInProgress: true });
            const { callback = () => {} } = this.props;
            const { saleStore } = this.props.stores;
            const { newSale } = saleStore;

            saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('lost'));

            await saleStore.saveSale({ calledFrom: 'salePayment.saveInvoiceAsLost', event: e, fieldsToSave: ['status'] });
            this.setState({ paymentInProgress: false });
            callback(true);
        } finally {
            dialogAction.finish();
        }
    }

    savePriceOffer = async e => {
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('priceOffer'));

        await saleStore.saveSale({ calledFrom: 'salePayment.savePriceOffer', event: e, fieldsToSave: ['status'] });
        this.setState({ paymentInProgress: false });
        callback(true);
    }

    saveCollectiveInvoice = async e => {
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('collectiveInvoice'));
        // paymentMethod must be included so the backend patch-sale trigger runs applyLogic
        // (vaccine reminders / innkallinger + stock update). Same body as
        // allVisitsTable.markAsCollectiveInvoice - without it, products on samlefaktura
        // sub-invoices never get their innkalling created.
        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'collectiveInvoice');

        await saleStore.saveSale({ calledFrom: 'salePayment.saveCollectiveInvoice', event: e, fieldsToSave: ['paymentMethod', 'status'] });
        this.setState({ paymentInProgress: false });
        callback(true);
    }

    convertToSale = async e => {
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;

        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('inProgress'));
        await saleStore.saveSale({ calledFrom: 'salePayment.saveCollectiveInvoice', event: e, fieldsToSave: ['status'] });
        this.setState({ paymentInProgress: false });
        callback(true);
    }

    openSendInsuranceClaim = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('sendInsuranceClaim', {
            height: drawerHeightMedium,
            newSale,
            callback: () => { this.forceUpdate(); },
        }, drawerLevel + 1);
    }

    onChangeCoveredAmount = e => {
        this.setState({ coveredAmount: e.target.value });
    }

    onChangeInsurancePaidDate = e => {
        this.setState({ insurancePaidDate: e.target.value });
    }

    onChangeEgenandelMode = e => {
        this.setState({ egenandelMode: e.target.value });
    }

    cancelInsuranceClaim = async () => {
        const dialogAction = beginDialogAction(this, 'cancelInsuranceClaim', () => [
            this.props?.stores?.saleStore?.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {
            if (!((await appConfirm('Er du sikker på at du vil angre kravet og kunne sende det på nytt? Forsikringsselskapet får ikke beskjed automatisk — du må selv kontakte dem om det opprinnelige kravet.')) && dialogAction.isCurrent())) {
                return;
            }
            const { saleStore } = this.props.stores;
            const { newSale } = saleStore;
            this.setState({ insuranceSubmitting: true, insuranceError: null });
            const response = await util.fetchApi(
                `/api/sales/${newSale.id}/insurance-claim/cancel`,
                { method: 'POST' },
                {},
            );
            if (response && response.status === 200) {
                saleStore.updateObjectKeyValue('newSale', 'status', 0);
                saleStore.updateObjectKeyValue('newSale', 'insuranceStatus', 0);
                saleStore.updateObjectKeyValue('newSale', 'insuranceClaim', null);
                this.setState({ insuranceSubmitting: false });
            } else {
                this.setState({
                    insuranceSubmitting: false,
                    insuranceError: (response && response.error) || 'Kunne ikke angre kravet.',
                });
            }
        } finally {
            dialogAction.finish();
        }
    }

    // TICKET-01637: "Angre oppgjøret". Hides the two child sales (and the insurance
    // payment) created by register-payment and puts the krav back to "krav sendt" so
    // the amounts can be registered again. The backend refuses (409) when a child has
    // reached the accounting system or the egenandel has been paid.
    undoInsuranceSettlement = async () => {
        const dialogAction = beginDialogAction(this, 'undoInsuranceSettlement', () => [
            this.props?.stores?.saleStore?.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {
            if (!((await appConfirm('Er du sikker på at du vil angre oppgjøret? Salget for forsikringsdekningen og egenandelen fjernes, og kravet settes tilbake til «Krav sendt» slik at du kan registrere beløpene på nytt.')) && dialogAction.isCurrent())) {
                return;
            }
            const { saleStore } = this.props.stores;
            const { newSale } = saleStore;
            this.setState({ insuranceSubmitting: true, insuranceError: null });
            const response = await util.fetchApi(
                `/api/sales/${newSale.id}/insurance-claim/undo-settlement`,
                { method: 'POST' },
                {},
            );
            if (response && response.status === 200) {
                const restoredClaim = (response.data && response.data.insuranceClaim) || null;
                saleStore.updateObjectKeyValue('newSale', 'status', 1007);
                saleStore.updateObjectKeyValue('newSale', 'insuranceStatus', (response.data && response.data.insuranceStatus) || 2);
                saleStore.updateObjectKeyValue('newSale', 'insuranceClaim', restoredClaim);
                this.loadSalePayments(newSale.id);
                this.setState({ insuranceSubmitting: false, coveredAmount: '' });
            } else {
                this.setState({
                    insuranceSubmitting: false,
                    insuranceError: (response && response.error) || 'Kunne ikke angre oppgjøret.',
                });
            }
        } finally {
            dialogAction.finish();
        }
    }

    // TICKET-01734: "Godkjent av forsikring" — the step between "Krav sendt" and
    // "Registrer innbetaling". The insurer's decision comes with the covered amount, so
    // the egenandel sale to the owner is created right away; the insurer's payment is
    // registered later with a date only. The krav stays locked until then.
    approveInsuranceClaim = async () => {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const total = saleStore.sumTotal(newSale.products, false);
        const resolved = resolveCoveredAmountInput({
            rawValue: this.state.coveredAmount,
            rejectAll: false,
            total,
        });
        if (!resolved.ok) {
            this.setState({ insuranceError: resolved.error });
            return;
        }
        const egenandelMode = this.state.egenandelMode === 'inProgress' ? 'inProgress' : 'invoice';
        this.setState({ insuranceSubmitting: true, insuranceError: null });
        const response = await util.fetchApi(
            `/api/sales/${newSale.id}/insurance-claim/approve`,
            { method: 'POST' },
            { approved: true, coveredAmount: resolved.coveredAmount, egenandelMode },
        );
        if (response && response.status === 200 && response.data) {
            saleStore.updateObjectKeyValue('newSale', 'insuranceStatus', response.data.insuranceStatus);
            saleStore.updateObjectKeyValue('newSale', 'insuranceClaim', response.data.insuranceClaim);
            this.setState({ insuranceSubmitting: false, coveredAmount: '' });
        } else {
            this.setState({
                insuranceSubmitting: false,
                insuranceError: (response && response.error) || 'Kunne ikke lagre godkjenningen.',
            });
        }
    }

    // "Angre godkjenning": hides the egenandel sale again (only while it is untouched)
    // and puts the krav back to "Krav sendt". The backend refuses (409) once the
    // egenandel is paid or exported.
    undoInsuranceClaimApproval = async () => {
        const dialogAction = beginDialogAction(this, 'undoInsuranceClaimApproval', () => [
            this.props?.stores?.saleStore?.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {
            if (!((await appConfirm('Er du sikker på at du vil angre godkjenningen? Egenandelsalget til eier fjernes, og kravet settes tilbake til «Krav sendt».')) && dialogAction.isCurrent())) {
                return;
            }
            const { saleStore } = this.props.stores;
            const { newSale } = saleStore;
            this.setState({ insuranceSubmitting: true, insuranceError: null });
            const response = await util.fetchApi(
                `/api/sales/${newSale.id}/insurance-claim/approve`,
                { method: 'POST' },
                { approved: false },
            );
            if (response && response.status === 200 && response.data) {
                saleStore.updateObjectKeyValue('newSale', 'insuranceStatus', response.data.insuranceStatus);
                saleStore.updateObjectKeyValue('newSale', 'insuranceClaim', response.data.insuranceClaim);
                this.setState({ insuranceSubmitting: false });
            } else {
                this.setState({
                    insuranceSubmitting: false,
                    insuranceError: (response && response.error) || 'Kunne ikke angre godkjenningen.',
                });
            }
        } finally {
            dialogAction.finish();
        }
    }

    registerInsurancePayment = async (rejectAll = false) => {
        const dialogAction = beginDialogAction(this, 'registerInsurancePayment', () => [
            this.props?.stores?.saleStore?.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { saleStore } = this.props.stores;
            const { newSale } = saleStore;
            const total = saleStore.sumTotal(newSale.products, false);
            const claim = newSale.insuranceClaim || {};
            // TICKET-01734: approved with the amount → the decision is already on the
            // krav and the egenandel sale exists. Only the paid date is sent.
            const approvedWithAmount = claim.approvedAmount != null;
            let coveredAmount;
            if (approvedWithAmount) {
                coveredAmount = claim.approvedAmount;
            } else {
                // TICKET-01209: an empty amount field must NOT fall back to 0 — the
                // backend books 0 as a rejection and creates a full-amount
                // egenandelsfaktura. 0 kr may only come from the rejection button.
                const resolved = resolveCoveredAmountInput({
                    rawValue: this.state.coveredAmount,
                    rejectAll,
                    total,
                });
                if (!resolved.ok) {
                    this.setState({ insuranceError: resolved.error });
                    return;
                }
                coveredAmount = resolved.coveredAmount;
            }
            const paidDate = this.state.insurancePaidDate || util.isoDate(new Date(), false, false, true);
            const egenandelMode = approvedWithAmount
                ? (claim.egenandelMode || 'invoice')
                : (this.state.egenandelMode === 'inProgress' ? 'inProgress' : 'invoice');

            const rejectConfirmText = egenandelMode === 'inProgress'
                ? 'Er du sikker på at forsikringen ikke dekket noe av kravet? Hele beløpet blir lagt som et pågående salg på eier, og dette kan ikke angres.'
                : 'Er du sikker på at forsikringen ikke dekket noe av kravet? Hele beløpet blir laget som egenandelsfaktura til eier, og dette kan ikke angres.';
            if (rejectAll && !((await appConfirm(rejectConfirmText)) && dialogAction.isCurrent())) {
                return;
            }

            this.setState({ insuranceSubmitting: true, insuranceError: null });
            const response = await util.fetchApi(
                `/api/sales/${newSale.id}/insurance-claim/register-payment`,
                { method: 'POST' },
                approvedWithAmount
                    ? { paidDate }
                    : {
                        coveredAmount, paidDate, confirmRejection: rejectAll === true, egenandelMode,
                    },
            );

            if (response && response.status === 200) {
                // Update local newSale state with the registered decision
                const updatedClaim = Object.assign({}, newSale.insuranceClaim || {}, {
                    coveredAmount,
                    decisionDate: new Date(paidDate),
                    insurancePaidSale: response.data && response.data.insurancePaidSale,
                    ownerInvoiceSale: response.data && response.data.ownerInvoiceSale,
                    egenandelMode: (response.data && response.data.egenandelMode) || egenandelMode,
                });
                saleStore.updateObjectKeyValue('newSale', 'insuranceClaim', updatedClaim);
                saleStore.updateObjectKeyValue('newSale', 'insuranceStatus', coveredAmount > 0 ? 3 : 4);
                saleStore.updateObjectKeyValue('newSale', 'status', 1008);
                this.loadSalePayments(newSale.id);
                this.setState({ insuranceSubmitting: false });
            } else {
                this.setState({
                    insuranceSubmitting: false,
                    insuranceError: (response && response.error) || 'Feil ved registrering.',
                });
            }
        } finally {
            dialogAction.finish();
        }
    }

    onClickPrintSale = e => {
        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalSaleReceipt stores={this.props.stores} />
        </IntlProvider>;

        // Render to a temporary div, extract HTML, then print via iframe
        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);

        // "Skriv ut" bookkeeping — see saleStore.markPrinted (skips status 0).
        const { saleStore } = this.props.stores;
        saleStore.markPrinted(saleStore.newSale && saleStore.newSale.id);
    }

    onClickPrintClaim = () => {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        if (!newSale || !newSale.id) {
            return;
        }
        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();
        window.open(`${apiServer}/api/sales/${newSale.id}/insurance-claim/pdf?jwtToken=${jwtToken}`, '_blank');
    }

    onClickPrintSykehistorie = () => {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        if (!newSale || !newSale.id) {
            return;
        }
        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();
        window.open(`${apiServer}/api/sales/${newSale.id}/insurance-claim/sykehistorie-pdf?jwtToken=${jwtToken}`, '_blank');
    }

    componentDidMount() {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        this.loadSalePayments(newSale.id);
        this.loadClaimContext(newSale);
    }

    renderInsuranceSection() {
        const { saleStore, userStore } = this.props.stores;
        const { newSale } = saleStore;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const { claimContextAnimal, claimContextHasJournals } = this.state;
        const firstAnimal = (newSale.animals && newSale.animals[0]) || claimContextAnimal || {};
        const claim = newSale.insuranceClaim;
        const hasJournals = (newSale.journals || []).filter(Boolean).length > 0 || !!claimContextHasJournals;

        // State C — settled (coveredAmount has been registered)
        if (claim && claim.coveredAmount != null) {
            const { insuranceSubmitting: undoSubmitting, insuranceError: undoError } = this.state || {};
            return (<>
                <div class='font-weight-lighter px-3 box-header mt-5'>
                    <Text id='dyrejournal.insurance-section'>Forsikring</Text>
                </div>
                <div class={`mx-3 px-3 py-3 mt-2 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                    <div>
                        <i class='fa-solid fa-check text-success me-2' />
                        <Text id='dyrejournal.insurance-decision-registered'>Dekning registrert</Text>
                        {claim.decisionDate ? <span class='ms-2'>{util.formatDate(claim.decisionDate, { locale: 'nb-NO' })}</span> : null}
                    </div>
                    {claim.approvedDate ? (
                        <div class='mt-2 text-body-secondary'>
                            <small>
                                <Text id='dyrejournal.insurance-approved'>Approved by the insurer</Text>
                                <span class='ms-2'>{util.formatDate(claim.approvedDate, { locale: 'nb-NO' })}</span>
                            </small>
                        </div>
                    ) : null}
                    <div class='mt-2'>
                        {claim.companyName || ''}: <strong>{util.format(claim.coveredAmount, 2, ',', ' ')} kr</strong>
                    </div>
                    {claim.insurancePaidSale ? (
                        <div class='mt-2'>
                            <Text id='dyrejournal.insurance-paid-sale'>Innbetaling fra forsikring</Text>: #{claim.insurancePaidSale}
                        </div>
                    ) : null}
                    {claim.ownerInvoiceSale ? (
                        <div class='mt-2'>
                            {claim.egenandelMode === 'inProgress' ? (
                                <Text id='dyrejournal.insurance-owner-sale-in-progress'>Egenandel (pågående salg)</Text>
                            ) : (
                                <Text id='dyrejournal.insurance-owner-invoice'>Egenandel-faktura</Text>
                            )}: #{claim.ownerInvoiceSale}
                        </div>
                    ) : null}
                    {!claim.ownerInvoiceSale && claim.coveredAmount > 0 ? (
                        <div class='mt-2 text-success'>
                            <Text id='dyrejournal.insurance-fully-covered'>Forsikringen dekket hele beløpet.</Text>
                        </div>
                    ) : null}
                    {claim.attachmentNames && claim.attachmentNames.length > 0 ? (
                        <div class='mt-2 text-body-secondary'>
                            <small>
                                <Text id='dyrejournal.insurance-attachments-sent'>Vedlegg sendt</Text>: {claim.attachmentNames.join(', ')}
                            </small>
                        </div>
                    ) : null}
                    <div class='mt-3'>
                        <button type='button' class='btn btn-outline-secondary btn-sm rounded-pill' onClick={this.onClickPrintClaim}>
                            <i class='fa-solid fa-print me-2' />
                            <Text id='dyrejournal.insurance-print-claim'>Skriv ut krav (PDF)</Text>
                        </button>
                        {hasJournals ? (
                            <button type='button' class='btn btn-outline-secondary btn-sm rounded-pill ms-2' onClick={this.onClickPrintSykehistorie}>
                                <i class='fa-solid fa-print me-2' />
                                <Text id='dyrejournal.insurance-print-sykehistorie'>Skriv ut sykehistorie (PDF)</Text>
                            </button>
                        ) : null}
                    </div>
                    <hr />
                    <div class='mt-2'>
                        <small class='text-body-secondary d-block mb-2'>
                            {claim.approvedAmount != null ? (
                                <Text id='dyrejournal.insurance-undo-settlement-help-approved'>Registrerte du feil dato? Du kan angre innbetalingen og registrere den på nytt. Egenandelen til eier beholdes. Det går bare så lenge forsikringssalget ikke er overført til regnskap.</Text>
                            ) : (
                                <Text id='dyrejournal.insurance-undo-settlement-help'>Registrerte du feil beløp? Du kan angre oppgjøret og registrere det på nytt. Det går bare så lenge ingen av de to salgene er betalt eller overført til regnskap.</Text>
                            )}
                        </small>
                        {undoError ? <div class='alert alert-danger'>{undoError}</div> : null}
                        <button
                            type='button'
                            class='btn btn-outline-danger btn-sm rounded-pill'
                            disabled={undoSubmitting}
                            onClick={this.undoInsuranceSettlement}
                        >
                            {undoSubmitting ? <i class='fa-duotone fa-spinner-scale me-2' /> : <i class='fa-solid fa-rotate-left me-2' />}
                            <Text id='dyrejournal.insurance-undo-settlement'>Angre oppgjøret</Text>
                        </button>
                    </div>
                </div>
            </>);
        }

        // State B — krav sendt (status === 1007, no coveredAmount yet). Two sub-states
        // (TICKET-01734): before the approval the clinic enters the covered amount and
        // the egenandel sale is created; after it only the insurer's paid date is left.
        if (newSale.status === 1007 && claim) {
            const total = saleStore.sumTotal(newSale.products, false);
            const {
                coveredAmount = '', insurancePaidDate, insuranceSubmitting, insuranceError, egenandelMode = 'invoice',
            } = this.state || {};
            const approvedWithAmount = claim.approvedAmount != null;
            const egenandelInProgress = approvedWithAmount
                ? claim.egenandelMode === 'inProgress'
                : egenandelMode === 'inProgress';
            const enteredAmount = parseFloat(String(coveredAmount).replace(',', '.'));
            const previewRest = Number.isFinite(enteredAmount) && enteredAmount > 0
                ? Math.max(0, total - enteredAmount)
                : null;
            const approvedRest = approvedWithAmount ? Math.max(0, total - claim.approvedAmount) : 0;
            return (<>
                <div class='font-weight-lighter px-3 box-header mt-5'>
                    <Text id='dyrejournal.insurance-section'>Forsikring</Text>
                </div>
                <div class={`mx-3 px-3 py-3 mt-2 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                    <div>
                        <i class='fa-solid fa-paper-plane text-info me-2' />
                        <Text id='dyrejournal.insurance-sent'>Krav sendt</Text>
                        {claim.sentDate ? <span class='ms-2'>{util.formatDate(claim.sentDate, { locale: 'nb-NO' })}</span> : null}
                        <Text id='dyrejournal.insurance-to'> til </Text>
                        {claim.companyName || ''}
                    </div>
                    {claim.sentTo ? (
                        <div class='mt-1 text-body-secondary'>
                            <small>{claim.sentTo}</small>
                        </div>
                    ) : null}
                    <div class='mt-2'>
                        <Text id='dyrejournal.insurance-total'>Total i krav</Text>:
                        {' '}<strong>{util.format(total, 2, ',', ' ')} kr</strong>
                    </div>
                    {approvedWithAmount ? (
                        <div class='mt-2'>
                            <div>
                                <i class='fa-solid fa-circle-check text-success me-2' />
                                <Text id='dyrejournal.insurance-approved'>Godkjent av forsikring</Text>
                                {claim.approvedDate ? <span class='ms-2'>{util.formatDate(claim.approvedDate, { locale: 'nb-NO' })}</span> : null}
                                <span class='ms-2'>
                                    <Text id='dyrejournal.insurance-approved-covers'>dekker</Text>
                                    {' '}<strong>{util.format(claim.approvedAmount, 2, ',', ' ')} kr</strong>
                                </span>
                            </div>
                            {claim.ownerInvoiceSale ? (
                                <div class='mt-1'>
                                    <Text id='dyrejournal.insurance-egenandel-amount'>Egenandel til eier</Text>:
                                    {' '}<strong>{util.format(approvedRest, 2, ',', ' ')} kr</strong>
                                    <span class='ms-2 text-body-secondary'>
                                        {egenandelInProgress ? (
                                            <Text id='dyrejournal.insurance-owner-sale-in-progress'>Egenandel (pågående salg)</Text>
                                        ) : (
                                            <Text id='dyrejournal.insurance-owner-invoice'>Egenandel-faktura</Text>
                                        )}: #{claim.ownerInvoiceSale}
                                    </span>
                                </div>
                            ) : (
                                <div class='mt-1 text-success'>
                                    <Text id='dyrejournal.insurance-fully-covered'>Forsikringen dekket hele beløpet.</Text>
                                </div>
                            )}
                            <small class='text-body-secondary d-block mt-1'>
                                <Text id='dyrejournal.insurance-approved-undo-help'>Feil beløp? Du kan angre godkjenningen så lenge egenandelen ikke er betalt eller overført til regnskap.</Text>
                                {' '}
                                <button
                                    type='button'
                                    class='btn btn-link btn-sm p-0 align-baseline'
                                    disabled={insuranceSubmitting}
                                    onClick={this.undoInsuranceClaimApproval}
                                >
                                    <Text id='dyrejournal.insurance-approved-undo'>Angre godkjenning</Text>
                                </button>
                            </small>
                        </div>
                    ) : null}
                    {claim.attachmentNames && claim.attachmentNames.length > 0 ? (
                        <div class='mt-2 text-body-secondary'>
                            <small>
                                <Text id='dyrejournal.insurance-attachments-sent'>Vedlegg sendt</Text>: {claim.attachmentNames.join(', ')}
                            </small>
                        </div>
                    ) : null}
                    <div class='mt-2'>
                        <button type='button' class='btn btn-outline-secondary btn-sm rounded-pill' onClick={this.onClickPrintClaim}>
                            <i class='fa-solid fa-print me-2' />
                            <Text id='dyrejournal.insurance-print-claim'>Skriv ut krav (PDF)</Text>
                        </button>
                        {hasJournals ? (
                            <button type='button' class='btn btn-outline-secondary btn-sm rounded-pill ms-2' onClick={this.onClickPrintSykehistorie}>
                                <i class='fa-solid fa-print me-2' />
                                <Text id='dyrejournal.insurance-print-sykehistorie'>Skriv ut sykehistorie (PDF)</Text>
                            </button>
                        ) : null}
                    </div>

                    {approvedWithAmount ? (<>
                        <div class='mt-3'>
                            <label class='form-label'>
                                <Text id='dyrejournal.insurance-paid-date'>Innbetalt dato</Text>
                            </label>
                            <input
                                type='date'
                                class='form-control'
                                value={insurancePaidDate || util.isoDate(new Date(), false, false, true)}
                                onInput={this.onChangeInsurancePaidDate}
                            />
                        </div>

                        {insuranceError ? <div class='alert alert-danger mt-3'>{insuranceError}</div> : null}

                        <button
                            type='button'
                            class='btn btn-success rounded-pill mt-3'
                            disabled={insuranceSubmitting}
                            onClick={() => this.registerInsurancePayment(false)}
                        >
                            {insuranceSubmitting ? <i class='fa-duotone fa-spinner-scale me-2' /> : null}
                            <Text id='dyrejournal.insurance-register-paid'>Registrer innbetaling fra forsikring</Text>
                        </button>
                        <small class='text-body-secondary d-block mt-1'>
                            <Text id='dyrejournal.insurance-register-paid-help'>Bruk denne når pengene fra forsikringsselskapet faktisk har kommet inn på konto.</Text>
                        </small>
                    </>) : (<>
                        <div class='mt-3'>
                            <label class='form-label'>
                                <Text id='dyrejournal.insurance-approved-amount'>Beløp forsikringen dekker (kr)</Text>
                            </label>
                            <input
                                type='number'
                                class='form-control'
                                value={coveredAmount}
                                onInput={this.onChangeCoveredAmount}
                                placeholder='0,00'
                                step='0.01'
                                min='0'
                            />
                            {previewRest != null ? (
                                <small class='text-body-secondary d-block mt-1'>
                                    <Text id='dyrejournal.insurance-egenandel-amount'>Egenandel til eier</Text>:
                                    {' '}<strong>{util.format(previewRest, 2, ',', ' ')} kr</strong>
                                </small>
                            ) : null}
                        </div>

                        <div class='mt-2'>
                            <label class='form-label'>
                                <Text id='dyrejournal.insurance-egenandel-mode'>Egenandelen til eier</Text>
                            </label>
                            <div class='form-check'>
                                <input
                                    class='form-check-input'
                                    type='radio'
                                    name='egenandelMode'
                                    id='egenandelModeInvoice'
                                    value='invoice'
                                    checked={!egenandelInProgress}
                                    onChange={this.onChangeEgenandelMode}
                                />
                                <label class='form-check-label' for='egenandelModeInvoice'>
                                    <Text id='dyrejournal.insurance-egenandel-invoice'>Lag faktura til eier</Text>
                                </label>
                            </div>
                            <div class='form-check'>
                                <input
                                    class='form-check-input'
                                    type='radio'
                                    name='egenandelMode'
                                    id='egenandelModeInProgress'
                                    value='inProgress'
                                    checked={egenandelInProgress}
                                    onChange={this.onChangeEgenandelMode}
                                />
                                <label class='form-check-label' for='egenandelModeInProgress'>
                                    <Text id='dyrejournal.insurance-egenandel-in-progress'>Pågående salg (betales i kassen med kort, kontant, Vipps eller faktura)</Text>
                                </label>
                            </div>
                        </div>

                        {insuranceError ? <div class='alert alert-danger mt-3'>{insuranceError}</div> : null}

                        <button
                            type='button'
                            class='btn btn-success rounded-pill mt-3'
                            disabled={insuranceSubmitting}
                            onClick={this.approveInsuranceClaim}
                        >
                            {insuranceSubmitting ? <i class='fa-duotone fa-spinner-scale me-2' /> : <i class='fa-solid fa-circle-check me-2' />}
                            {egenandelInProgress ? (
                                <Text id='dyrejournal.insurance-approve-in-progress'>Godkjent av forsikring, lag pågående salg for egenandel</Text>
                            ) : (
                                <Text id='dyrejournal.insurance-approve'>Godkjent av forsikring, lag egenandelsfaktura</Text>
                            )}
                        </button>
                        <small class='text-body-secondary d-block mt-1'>
                            <Text id='dyrejournal.insurance-approve-help'>Bruk denne når forsikringsselskapet har godkjent kravet og sagt hvor mye de dekker. Egenandelen til eier lages med en gang. Innbetalingen fra forsikringen registrerer du her når pengene har kommet.</Text>
                        </small>
                        <div class='mt-2'>
                            <button
                                type='button'
                                class='btn btn-link'
                                disabled={insuranceSubmitting}
                                onClick={() => this.registerInsurancePayment(true)}
                            >
                                {egenandelInProgress ? (
                                    <Text id='dyrejournal.insurance-rejected-in-progress'>Forsikring dekket ingenting — hele beløpet som pågående salg til eier</Text>
                                ) : (
                                    <Text id='dyrejournal.insurance-rejected'>Forsikring dekket ingenting — faktura hele beløpet til eier</Text>
                                )}
                            </button>
                        </div>
                        <hr />
                        <div class='mt-2'>
                            <small class='text-body-secondary d-block mb-2'>
                                <Text id='dyrejournal.insurance-cancel-help'>Sendte du kravet med feil mottaker eller feil informasjon? Du kan angre kravet og sende det på nytt. Husk å kontakte forsikringsselskapet om det første kravet.</Text>
                            </small>
                            <button
                                type='button'
                                class='btn btn-outline-danger btn-sm rounded-pill'
                                disabled={insuranceSubmitting}
                                onClick={this.cancelInsuranceClaim}
                            >
                                <i class='fa-solid fa-rotate-left me-2' />
                                <Text id='dyrejournal.insurance-cancel'>Angre kravet og send på nytt</Text>
                            </button>
                        </div>
                    </>)}
                </div>
            </>);
        }

        // State A — pre-send
        return (<>
            <div class='font-weight-lighter px-3 box-header mt-5'>
                <Text id='dyrejournal.insurance-section'>Forsikring</Text>
            </div>
            <div class={`mx-3 px-3 py-3 mt-2 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                <div>
                    <Text id='dyrejournal.insurance-company'>Forsikringsselskap</Text>: <strong>{firstAnimal.insuranceCompany || '—'}</strong>
                </div>
                {firstAnimal.insuranceId ? (
                    <div>
                        <Text id='dyrejournal.insurance-id'>Forsikrings-ID</Text>: <strong>{firstAnimal.insuranceId}</strong>
                    </div>
                ) : null}

                <button type='button' class='btn btn-warning rounded-pill mt-3' onClick={this.openSendInsuranceClaim}>
                    <i class='fa-solid fa-paper-plane me-2' />
                    <Text id='dyrejournal.insurance-send-claim'>Send som krav til forsikring</Text>
                </button>
            </div>
        </>);
    }

    render() {
        const { week, isNew, drawerLevel } = this.props;
        const { sections, partialPayment, payDate, paymentInProgress } = this.state;
        const { userStore, saleStore } = this.props.stores;
        const { customer, user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const {
            saleTotal = 0,
            saleTax = 0,
            newSale = {},
            currentPayments = [],
        } = saleStore;

        const saleIsLocked = !!newSale.paymentMethod;
        const isKravSale = newSale.status === 1007 || newSale.status === 1008;
        const restPayment = saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false), false, false);

        const { featureFlags = {} } = customer;

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        <Text id='dyrejournal.sale-other'>Sale Other</Text>
                    </div>
                    <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>

                        <div class='d-flex flex-column mt-0 pt-2 pe-5' style='font-size: 1.5em;'>
                            <div class='d-flex flex-row justify-content-end'>
                                <div><Text id='dyrejournal.payment-total'>Total</Text></div>
                                <div class='ms-3 text-success font-weight-bold'>{util.format(saleTotal, 2, ',', ' ')}</div>
                            </div>
                            <div class='d-flex flex-row justify-content-end font-weight-lighter'>
                                <div><Text id='dyrejournal.payment-tax'>Tax</Text></div>
                                <div class='ms-3'>{saleTax}</div>
                            </div>
                            {!isKravSale && (
                                <div class='d-flex flex-row justify-content-end font-weight-bold text-danger'>
                                    <div><Text id='dyrejournal.payment-rest'>Rest payment</Text></div>
                                    <div class='ms-3'>{util.format(restPayment, 2)}</div>
                                </div>
                            )}
                        </div>

                        {!newSale.id ? <>
                            <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                <div class='spinner-grow text-secondary' role='status'>
                                    <span class='sr-only'>Loading...</span>
                                </div>
                            </div>
                        </> : <>
                            {saleIsLocked ? <>
                                <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                    <Text id='dyrejournal.sale-is-lost-help'>If sale is considered lost you can click the button below to mark the save as lost. Sale will be put into your cost account and you can claim the tax for this sale. Important! Make sure you follow the national tax rules for this.</Text>
                                </div>
                                <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                    <button type='button' class='btn btn-danger rounded-pill ms-3' disabled={paymentInProgress} onClick={this.saveInvoiceAsLost}>
                                        {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('lost')} /> <Text id='dyrejournal.sale-is-lost'>Mark sale as lost</Text>
                                    </button>
                                </div>
                            </> : <>
                                <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                    {newSale.status === 1003 || isKravSale ? <>
                                    </> : <>
                                        {newSale.status === 1001 ? <>
                                            <button type='button' class='btn btn-warning rounded-pill ms-3' disabled={paymentInProgress} onClick={this.sendInvoice}>
                                                {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('priceOffer')} /> <Text id='dyrejournal.sale-send-priceoffer'>Send Price Offer</Text>
                                            </button>
                                        </> : <>
                                            <button type='button' class='btn btn-warning rounded-pill ms-3' disabled={paymentInProgress} onClick={this.savePriceOffer}>
                                                {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('priceOffer')} /> <Text id='dyrejournal.sale-save-priceoffer'>Save Price Offer</Text>
                                            </button>
                                        </>}
                                        <button type='button' class='btn btn-info rounded-pill ms-3' disabled={paymentInProgress} onClick={this.saveCollectiveInvoice}>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('collectiveInvoice')} /> <Text id='dyrejournal.sale-create-collectiveinvoice'>Save to collective invoice</Text>
                                        </button>
                                    </>}

                                    <button
                                        class={`btn btn-secondary ms-2 rounded-pill`}
                                        onClick={this.onClickPrintSale}
                                    >
                                        <i class='fa-solid fa-print' /> <Text id='dyrejournal.print-sale'>Print sale</Text>
                                        <PrintedMark sale={newSale} />
                                    </button>

                                    {newSale.status === 2 ? <>
                                        <button type='button' class='btn btn-danger rounded-pill ms-3' disabled={paymentInProgress} onClick={this.saveInvoiceAsLost}>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('lost')} /> <Text id='dyrejournal.sale-is-lost'>Mark sale as lost</Text>
                                        </button>
                                    </> : <></>}
                                </div>

                                {newSale.status === 1003 ? <>
                                    <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                        <button type='button' class='btn btn-link  rounded-pill ms-3' onClick={this.unlockPaymentMethod}>
                                            <i class='fa-solid fa-lock' /> <Text id='dyrejournal.sale-unlock-payment-method'>Unlock payment method</Text>
                                        </button>
                                    </div>
                                </> : <></>}

                                {newSale.status === 1001 ? <>
                                    <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                        <button type='button' class='btn btn-success rounded-pill ms-3' disabled={paymentInProgress} onClick={this.convertToSale}>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class='fa-duotone fa-wand-magic-sparkles' /> <Text id='dyrejournal.priceoffer-convert-to-sale'>Convert to sale</Text>
                                        </button>
                                    </div>
                                </> : <></>}
                            </>}

                            {this.renderInsuranceSection()}

                            {currentPayments && currentPayments.length > 0 && <>
                                <div class='font-weight-lighter px-3 box-header mt-5'>
                                    <Text id='dyrejournal.existing-sale-payments'>Existing Payments</Text>
                                </div>
                                <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                                    <div class='w-100 d-flex flex-column'>
                                        <div class={`d-flex flex-column mx-3 px-2 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                            <small>
                                            <div class={`table-responsive ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                                <table class='table table-striped table-sm mb-0'>
                                                    <thead>
                                                        <tr>
                                                            <th class='text-muted text-start'><nobr><Text id='dyrejournal.partial-payment-date'>Date</Text></nobr></th>
                                                            <th class='text-muted text-center'><nobr><Text id='dyrejournal.partial-payment-method'>Method</Text></nobr></th>
                                                            <th class='text-muted text-end'><nobr><Text id='dyrejournal.sale.amount'>Amount</Text></nobr></th>
                                                            <th>&nbsp;</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentPayments.map((e, idx) => <>
                                                            <tr data-id={e.id}>
                                                                <td>{util.formatDate(e.paidDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</td>
                                                                <td class='text-center'><i class={saleStore.getPaymentMethodIcon(e.paymentMethod)} /> {e.paymentMethod}</td>
                                                                <td class='text-end'>{util.format(e.amount, 2, ',', ' ')}</td>
                                                                <td class='d-none d-sm-table-cell' onClick={this.onClickDeleteSalePayment} data-id={e.id}>
                                                                    <i class='fa-solid fa-trash text-danger' />
                                                                </td>
                                                            </tr>
                                                        </>)}
                                                    </tbody>
                                                </table>
                                            </div>
                                            </small>
                                            {/* <xmp>{JSON.stringify(currentPayments, null, 4)}</xmp> */}
                                        </div>
                                    </div>
                                </div>
                            </>}

                            {newSale.reminders && newSale.reminders.length > 0 && <>
                                <div class='w-100 d-flex flex-sm-row flex-column'>
                                    <div class='w-100 d-flex flex-column'>
                                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mt-3'>
                                            {newSale.status === 1001 ? <>
                                                <span><Text id='dyrejournal.payment-priceoffers'>Priceoffer Emails</Text></span>
                                            </> : <>
                                                <span><Text id='dyrejournal.payment-reminders'>Payment reminders</Text></span>
                                            </>}
                                        </div>
                                        <div class={`d-flex flex-column mx-3 px-2 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                            <div class={`table-responsive ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg border`}>
                                                <table class='table table-striped table-sm mb-0'>
                                                    <thead>
                                                        <tr>
                                                            <th class='text-muted text-start'><nobr><Text id='dyrejournal.reminder-date'>Date</Text></nobr></th>
                                                            <th class='text-muted text-center'><nobr><Text id='dyrejournal.reminder-method'>Method</Text></nobr></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {newSale.reminders.map((e, idx) => <>
                                                            <tr>
                                                                <td>{util.formatDate(e.date, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</td>
                                                                <td class='text-center'><i class={saleStore.getReminderMethodIcon(e.type)} /> {e.type}</td>
                                                            </tr>
                                                        </>)}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {/* <xmp>{JSON.stringify(newSale.partialPayments, null, 4)}</xmp> */}
                                        </div>
                                    </div>
                                </div>
                            </>}

                        </>}

                    </div>
                </div>
            </div>
        </>);
    }
}

export default SalePaymentOther;
